/**
 * Format country code for display (case-insensitive).
 */
export function formatCountry(code) {
  if (!code) return "";
  const c = String(code).toLowerCase();
  if (c === "ca") return "Canada";
  if (c === "us") return "United States";
  return code;
}

/** Compare address lines ignoring case, commas and spacing:
 *  "Markham ON L6G0H7" ≡ "Markham, ON, L6G0H7". */
const normalizeLine = (line) =>
  String(line || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * Build display fields for an address (name, street, city/state/zip, country, phone).
 * Handles both addrText (name on first line) and raw address fields.
 *
 * `addrText` can be TWO very different things, and the dedupe below is what keeps
 * them from double-printing:
 *
 * - locally-added addresses carry the app's own `[name, street, apt]` — short, no
 *   city — so the discrete cityStateZip/country lines still render;
 * - addresses seeded from the NetSuite profile carry NetSuite's FULL formatted
 *   block (addressee \ street \ "City ON L6G0H7" \ "Canada"). Rendering that and
 *   then appending the discrete fields printed the city and country twice — which
 *   went unnoticed for as long as saves never reached NetSuite and no full block
 *   ever round-tripped back.
 *
 * So: a discrete line is suppressed when the addrText already contains it
 * (normalized). The name is stripped off addrText's first line only when it
 * matches `fullName` — a differing first line is the address's own addressee,
 * which is real information.
 *
 * `phone` filters the "Phone not available" placeholder `useInitialAddress` bakes
 * into seeded data, so consumers can simply `{phone && …}`.
 */
export function getAddressDisplayFields(address) {
  if (!address) return null;

  const name = address.fullName ?? address.fullname ?? "";

  let streetLines = "";
  if (address.addrText) {
    const lines = address.addrText.split("\n");
    const firstLineIsName = name && lines[0] === name;
    streetLines = firstLineIsName
      ? lines.slice(1).join("\n")
      : address.addrText;
  } else {
    streetLines = [address.address, address.address2]
      .filter(Boolean)
      .join("\n");
  }

  const streetNormalized = streetLines.split("\n").map(normalizeLine);
  const includesLine = (line) =>
    Boolean(line) && streetNormalized.includes(normalizeLine(line));

  let cityStateZip = [address.city, address.state, address.zip]
    .filter(Boolean)
    .join(", ");
  if (includesLine(cityStateZip)) cityStateZip = "";

  let countryDisplay = formatCountry(address.country);
  if (includesLine(countryDisplay)) countryDisplay = "";

  const phone =
    address.phone && address.phone !== "Phone not available"
      ? address.phone
      : "";

  return { name, streetLines, cityStateZip, countryDisplay, phone };
}
